import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { QRCodeSVG } from 'qrcode.react';
import axios from 'axios';
import { toast } from 'react-toastify';
import usePlan from '../hooks/usePlan';
import FeatureNotice from './FeatureNotice';
import { GST_STATE_NAMES } from '../utils/countriesStates';

import DateInput from './DateInput';
import { formatDateTime } from '../utils/dateFormat';
const errorMessage = (error, fallback) => {
  const data = error.response?.data;
  return typeof data === 'string' && data ? data : fallback;
};
const when = (iso) => (iso ? formatDateTime(iso) : '-');

/** Whether a server time (local, no zone) has passed. */
const pastDeadline = (iso) => Boolean(iso) && new Date(String(iso)) < new Date();

const CANCEL_REASONS = [
  { value: '1', label: 'Duplicate' },
  { value: '2', label: 'Data entry mistake' },
  { value: '3', label: 'Order cancelled' },
  { value: '4', label: 'Other' }
];

export const SANDBOX_NOTE = 'SANDBOX - not a government registration. Nothing was sent to the GST portal.';

/**
 * IRN / e-way bill state for the documents in a list, keyed by document id.
 * Returns [summaries, reload].
 */
export function useGstSummaries(docType, ids, enabled = true) {
  const [summaries, setSummaries] = useState({});
  const key = ids.join(',');

  const reload = useCallback(() => {
    if (!enabled || !key) {
      setSummaries({});
      return;
    }
    axios.get(`/api/gst-api/documents/${docType}/summary`, { params: { ids: key } })
      .then((r) => setSummaries(r.data || {}))
      .catch(() => setSummaries({}));
  }, [docType, key, enabled]);

  useEffect(() => { reload(); }, [reload]);
  return [summaries, reload];
}

/** Small badges for a list row: IRN registered / cancelled, e-way bill number. */
export function GstRegistrationBadges({ summary }) {
  if (!summary) return null;
  return (
    <>
      {summary.irn && (
        <Badge bg={summary.einvoiceStatus === 'CANCELLED' ? 'secondary' : 'success'} className="me-1" title={summary.irn}>
          {summary.einvoiceStatus === 'CANCELLED' ? 'IRN cancelled' : 'IRN'}
        </Badge>
      )}
      {summary.ewbNo && (
        <Badge bg={summary.ewbStatus === 'CANCELLED' ? 'secondary' : 'info'} title={`Valid until ${when(summary.ewbValidUpto)}`}>
          {summary.ewbStatus === 'CANCELLED' ? 'EWB cancelled' : `EWB ${summary.ewbNo}`}
        </Badge>
      )}
    </>
  );
}

/** The IRN, acknowledgement, QR and e-way bill for a document, for the printed copy. Nothing when there is none. */
export function GstRegistrationPrintBlock({ docType, docId }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setStatus(null);
    if (!docId) return undefined;
    axios.get(`/api/gst-api/documents/${docType}/${docId}`)
      .then((r) => { if (!cancelled) setStatus(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [docType, docId]);

  const irn = status?.einvoice && status.einvoice.status === 'GENERATED' ? status.einvoice : null;
  const ewb = status?.eway && status.eway.status === 'ACTIVE' ? status.eway : null;
  if (!irn && !ewb) return null;

  return (
    <div className="border rounded p-2 mt-3 d-flex gap-3 align-items-start" style={{ breakInside: 'avoid' }}>
      {irn && irn.signedQr && (
        <div className="text-center" style={{ flex: '0 0 auto' }}>
          <QRCodeSVG value={irn.signedQr} size={104} level="M" />
        </div>
      )}
      <div className="small" style={{ minWidth: 0, wordBreak: 'break-all' }}>
        {irn && (
          <>
            <div><strong>IRN:</strong> {irn.irn}</div>
            <div><strong>Ack no:</strong> {irn.ackNo} &nbsp; <strong>Ack date:</strong> {when(irn.ackDate)}</div>
          </>
        )}
        {ewb && (
          <div className={irn ? 'mt-1' : ''}>
            <strong>E-way bill:</strong> {ewb.ewbNo} &nbsp; <strong>Valid until:</strong> {when(ewb.validUpto)}
            {ewb.vehicleNo ? <> &nbsp; <strong>Vehicle:</strong> {ewb.vehicleNo}</> : null}
          </div>
        )}
        {((irn && irn.sandbox) || (ewb && ewb.sandbox)) && (
          <div className="mt-1 fw-bold text-danger">{SANDBOX_NOTE}</div>
        )}
      </div>
    </div>
  );
}

const EMPTY_TRANSPORT = {
  mode: 'ROAD', vehicleNo: '', transporterId: '', transporterName: '', distanceKm: '', transportDocNo: '', transportDocDate: ''
};

const VEHICLE_REASONS = [
  { value: '1', label: 'Breakdown' },
  { value: '2', label: 'Transhipment' },
  { value: '4', label: 'First vehicle (none was given)' },
  { value: '3', label: 'Other' }
];
const EXTEND_REASONS = [
  { value: '5', label: 'Accident' },
  { value: '1', label: 'Natural calamity' },
  { value: '2', label: 'Law and order situation' },
  { value: '4', label: 'Transhipment' },
  { value: '99', label: 'Other' }
];
// Where goods can be: leave out codes no longer used for places (old Andhra Pradesh, Daman and Diu before the merger)
// and Centre Jurisdiction, which isn't a place
const RETIRED_STATE_CODES = ['25', '28', '99'];
const STATE_OPTIONS = Object.entries(GST_STATE_NAMES)
  .filter(([code]) => !RETIRED_STATE_CODES.includes(code))
  .sort((a, b) => a[1].localeCompare(b[1]));
const EXTEND_WINDOW_MS = 8 * 60 * 60 * 1000;

/**
 * Changing the vehicle on an active e-way bill (Part B) and extending its validity, with the history of both.
 * Validity can be extended from 8 hours before it ends to 8 hours after.
 */
function EwayChanges({ base, ewb, run, busy }) {
  const [mode, setMode] = useState(null); // 'vehicle' | 'extend'
  const [form, setForm] = useState({});
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    axios.get(`${base}/eway/updates`).then((r) => setHistory(Array.isArray(r.data) ? r.data : [])).catch(() => setHistory([]));
  }, [base]);
  useEffect(() => { loadHistory(); }, [loadHistory, ewb.validUpto, ewb.vehicleNo]);

  const ends = new Date(String(ewb.validUpto));
  const now = Date.now();
  const extendFrom = new Date(ends.getTime() - EXTEND_WINDOW_MS);
  const extendUntil = new Date(ends.getTime() + EXTEND_WINDOW_MS);
  const canExtend = now >= extendFrom.getTime() && now <= extendUntil.getTime();
  const expired = now > ends.getTime();
  const showTime = (d) => d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

  const open = (m) => {
    setMode(m);
    setForm(m === 'vehicle'
      ? { mode: 'ROAD', vehicleNo: '', fromPlace: '', fromStateCode: '', reasonCode: ewb.vehicleNo ? '1' : '4', remarks: '', transportDocNo: '', transportDocDate: '' }
      : { consignmentStatus: 'M', mode: 'ROAD', vehicleNo: ewb.vehicleNo || '', fromPlace: '', fromStateCode: '', fromPincode: '',
        remainingDistanceKm: '', reasonCode: '5', remarks: '', transitType: 'W', address1: '', address2: '', address3: '',
        transportDocNo: '', transportDocDate: '' });
  };
  const f = (name) => ({ value: form[name] ?? '', onChange: (e) => setForm({ ...form, [name]: e.target.value }) });
  const submit = () => {
    const body = { ...form, transportDocDate: form.transportDocDate || null };
    if (mode === 'extend') body.remainingDistanceKm = form.remainingDistanceKm === '' ? null : parseInt(form.remainingDistanceKm, 10);
    run(() => axios.post(`${base}/eway/${mode === 'vehicle' ? 'vehicle' : 'extend'}`, body),
      mode === 'vehicle' ? 'Vehicle changed on the e-way bill' : 'E-way bill validity extended', mode)
      .then((ok) => { if (ok) { setMode(null); loadHistory(); } });
  };

  const placeFields = (
    <>
      <Col md={4}>
        <Form.Label className="small mb-1">Goods are now at (place)</Form.Label>
        <Form.Control size="sm" maxLength={50} {...f('fromPlace')} />
      </Col>
      <Col md={4}>
        <Form.Label className="small mb-1">State</Form.Label>
        <Form.Select size="sm" {...f('fromStateCode')}>
          <option value="">Choose...</option>
          {STATE_OPTIONS.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
        </Form.Select>
      </Col>
    </>
  );
  const transportFields = (
    <>
      <Col md={3}>
        <Form.Label className="small mb-1">Mode</Form.Label>
        <Form.Select size="sm" {...f('mode')}>
          <option value="ROAD">Road</option>
          <option value="RAIL">Rail</option>
          <option value="AIR">Air</option>
          <option value="SHIP">Ship</option>
        </Form.Select>
      </Col>
      {form.mode === 'ROAD' ? (
        <Col md={4}>
          <Form.Label className="small mb-1">Vehicle number</Form.Label>
          <Form.Control size="sm" placeholder="KA01AB1234" {...f('vehicleNo')} />
        </Col>
      ) : (
        <>
          <Col md={3}>
            <Form.Label className="small mb-1">Transport doc no.</Form.Label>
            <Form.Control size="sm" maxLength={15} {...f('transportDocNo')} />
          </Col>
          <Col md={3}>
            <Form.Label className="small mb-1">Doc date</Form.Label>
            <DateInput size="sm" {...f('transportDocDate')} />
          </Col>
        </>
      )}
    </>
  );

  return (
    <div className="mt-2">
      {!mode && (
        <div className="d-flex flex-wrap gap-2 align-items-center">
          {!expired && (
            <Button size="sm" variant="outline-primary" disabled={Boolean(busy)} onClick={() => open('vehicle')}>
              {ewb.vehicleNo ? 'Change vehicle' : 'Add vehicle'}
            </Button>
          )}
          {canExtend ? (
            <Button size="sm" variant="outline-primary" disabled={Boolean(busy)} onClick={() => open('extend')}>Extend validity</Button>
          ) : now < extendFrom.getTime() ? (
            <span className="small text-muted">Validity can be extended from {showTime(extendFrom)} to {showTime(extendUntil)}.</span>
          ) : (
            <span className="small text-muted">The time to extend ended {showTime(extendUntil)}; a new e-way bill is needed for the rest of the journey.</span>
          )}
        </div>
      )}

      {mode === 'vehicle' && (
        <div className="border rounded p-2 mt-2">
          <div className="fw-bold small mb-2">{ewb.vehicleNo ? 'Change the vehicle' : 'Add the vehicle'}</div>
          <Row className="g-2">
            <Col md={5}>
              <Form.Label className="small mb-1">Reason</Form.Label>
              <Form.Select size="sm" {...f('reasonCode')}>
                {VEHICLE_REASONS.filter((r) => (ewb.vehicleNo ? r.value !== '4' : true)).map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Form.Select>
            </Col>
            {transportFields}
            {placeFields}
            <Col md={4}>
              <Form.Label className="small mb-1">Remarks{form.reasonCode === '3' ? '' : ' (optional)'}</Form.Label>
              <Form.Control size="sm" maxLength={50} {...f('remarks')} />
            </Col>
          </Row>
          <div className="d-flex gap-2 mt-2">
            <Button size="sm" disabled={Boolean(busy)} onClick={submit}>{busy === 'vehicle' ? 'Working...' : 'Save vehicle'}</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setMode(null)}>Close</Button>
          </div>
        </div>
      )}

      {mode === 'extend' && (
        <div className="border rounded p-2 mt-2">
          <div className="fw-bold small mb-2">Extend the validity</div>
          <Row className="g-2">
            <Col md={5}>
              <Form.Label className="small mb-1">Reason</Form.Label>
              <Form.Select size="sm" {...f('reasonCode')}>
                {EXTEND_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small mb-1">Km still to go</Form.Label>
              <Form.Control size="sm" type="number" min="1" max={ewb.distanceKm} {...f('remainingDistanceKm')} />
            </Col>
            <Col md={4}>
              <Form.Label className="small mb-1">The goods are</Form.Label>
              <Form.Select size="sm" {...f('consignmentStatus')}>
                <option value="M">Moving, on a vehicle</option>
                <option value="T">Held in transit</option>
              </Form.Select>
            </Col>
            {form.consignmentStatus === 'M' ? transportFields : (
              <>
                <Col md={3}>
                  <Form.Label className="small mb-1">Held</Form.Label>
                  <Form.Select size="sm" {...f('transitType')}>
                    <option value="W">In a warehouse</option>
                    <option value="R">On the road</option>
                    <option value="O">Elsewhere</option>
                  </Form.Select>
                </Col>
                <Col md={9}>
                  <Form.Label className="small mb-1">Address where they are held</Form.Label>
                  <Form.Control size="sm" maxLength={120} {...f('address1')} />
                </Col>
              </>
            )}
            {placeFields}
            <Col md={4}>
              <Form.Label className="small mb-1">Pin code</Form.Label>
              <Form.Control size="sm" maxLength={6} inputMode="numeric" {...f('fromPincode')} />
            </Col>
            <Col md={8}>
              <Form.Label className="small mb-1">Remarks{form.reasonCode === '99' ? '' : ' (optional)'}</Form.Label>
              <Form.Control size="sm" maxLength={50} {...f('remarks')} />
            </Col>
          </Row>
          <div className="d-flex gap-2 mt-2">
            <Button size="sm" disabled={Boolean(busy)} onClick={submit}>{busy === 'extend' ? 'Working...' : 'Extend validity'}</Button>
            <Button size="sm" variant="outline-secondary" onClick={() => setMode(null)}>Close</Button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="small mt-2">
          <div className="fw-bold">Changes</div>
          <ul className="mb-0 ps-3">
            {history.map((u) => (
              <li key={u.id}>
                {when(u.createdAt)}: {u.kind === 'EXTEND'
                  ? `validity extended to ${when(u.validUptoAfter)} (${u.remainingDistanceKm} km to go)`
                  : `vehicle ${u.vehicleNo || `by ${String(u.transportMode || '').toLowerCase()}`}`}
                {' '}from {u.fromPlace}
                {u.remarks ? ` - ${u.remarks}` : ''}
                {u.sandbox && <Badge bg="warning" text="dark" className="ms-1">Sandbox</Badge>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** The last day an IRN can be generated under the 30-day limit, and how many days are left. */
function ReportByNote({ date }) {
  const last = new Date(`${String(date).slice(0, 10)}T23:59:59`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysLeft = Math.round((new Date(`${String(date).slice(0, 10)}T00:00:00`) - today) / 86400000);
  const shown = last.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div className={`small mb-2 ${daysLeft <= 5 ? 'text-warning-emphasis fw-semibold' : 'text-muted'}`}>
      Register by {shown} ({daysLeft === 0 ? 'today is the last day' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}): the portal
      refuses e-invoices more than 30 days old for businesses with turnover of Rs 10 crore or more.
    </div>
  );
}

/** Register a document for e-invoicing and raise or cancel its e-way bill. */
export function GstRegistrationModal({ show, docType, docId, label, onHide, onChanged, apiBase, canCancel = true }) {
  const ewayOnlyDoc = !['SALE_BILL', 'SALE_NOTE'].includes(docType);
  // Sale bills and notes use the document paths; goods moved without a sale (transfers, job work, returns) pass their own
  const base = apiBase || `/api/gst-api/documents/${docType}/${docId}`;
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);
  const [problem, setProblem] = useState('');
  const [transport, setTransport] = useState(EMPTY_TRANSPORT);
  const [confirmBelow, setConfirmBelow] = useState(false);
  const [cancelling, setCancelling] = useState(null); // 'einvoice' | 'eway'
  const [reason, setReason] = useState('2');
  const [remarks, setRemarks] = useState('');

  const load = useCallback(() => {
    if (!docId) return;
    setLoading(true);
    axios.get(base)
      .then((r) => setStatus(r.data))
      .catch((e) => { setStatus(null); setProblem(errorMessage(e, 'Could not load the registration state')); })
      .finally(() => setLoading(false));
  }, [docId, base]);

  useEffect(() => {
    if (!show) return;
    setProblem('');
    setCancelling(null);
    setTransport(EMPTY_TRANSPORT);
    setConfirmBelow(false);
    setRemarks('');
    load();
  }, [show, load]);

  const run = async (request, success, key) => {
    setBusy(key);
    setProblem('');
    try {
      await request();
      toast.success(success);
      setCancelling(null);
      load();
      if (onChanged) onChanged();
      return true;
    } catch (e) {
      setProblem(errorMessage(e, 'The request failed'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const irn = status?.einvoice || null;
  const irnActive = irn && irn.status === 'GENERATED';
  const ewb = status?.eway || null;
  const ewbActive = ewb && ewb.status === 'ACTIVE';
  const einvoiceErrors = status?.einvoiceErrors || [];
  const sandbox = Boolean(status?.sandbox);

  const generateEwb = () => run(() => axios.post(`${base}/eway`, {
    ...transport,
    distanceKm: transport.distanceKm === '' ? null : parseInt(transport.distanceKm, 10),
    transportDocDate: transport.transportDocDate || null,
    confirmBelowThreshold: confirmBelow
  }), 'E-way bill generated', 'ewb');

  const field = (name) => ({
    value: transport[name],
    onChange: (e) => setTransport({ ...transport, [name]: e.target.value })
  });

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{ewayOnlyDoc ? 'e-Way bill' : 'e-Invoice and e-Way bill'}{label ? ` - ${label}` : ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && !status ? <div className="text-center"><Spinner animation="border" size="sm" /></div> : null}
        {problem && <Alert variant="danger" style={{ whiteSpace: 'pre-line' }}>{problem}</Alert>}
        {sandbox && (
          <Alert variant="warning" className="py-2 small">
            Sandbox mode: nothing is sent to the GST portal. The numbers made here are for testing only and are marked as such on print.
          </Alert>
        )}
        {status && (
          <>
            {!status.ewayOnly && (
              <>
            <h6>e-Invoice (IRN)</h6>
            {!status.einvoiceEnabled && !irn && (
              <p className="text-muted small">E-invoicing is switched off. Turn it on in Org settings &gt; GST Tax Profile.</p>
            )}
            {irn && (
              <div className="small mb-2" style={{ wordBreak: 'break-all' }}>
                <div>
                  <Badge bg={irnActive ? 'success' : 'secondary'} className="me-2">{irnActive ? 'Registered' : 'Cancelled'}</Badge>
                  {irn.sandbox && <Badge bg="warning" text="dark">Sandbox</Badge>}
                </div>
                <div className="mt-1"><strong>IRN:</strong> {irn.irn}</div>
                <div><strong>Ack no:</strong> {irn.ackNo} &nbsp; <strong>Ack date:</strong> {when(irn.ackDate)}</div>
                {!irnActive && <div><strong>Cancelled:</strong> {when(irn.cancelledAt)}</div>}
              </div>
            )}
            {status.einvoiceEnabled && !irnActive && einvoiceErrors.length > 0 && (
              <Alert variant={irn ? 'secondary' : 'warning'} className="small py-2">
                <div className="fw-bold mb-1">{irn ? 'Note' : 'Fix these before registering'}</div>
                <ul className="mb-0 ps-3">
                  {einvoiceErrors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </Alert>
            )}
            {status.einvoiceReportBy && !irn && einvoiceErrors.length === 0 && (
              <ReportByNote date={status.einvoiceReportBy} />
            )}
            {status.einvoiceEnabled && !irn && einvoiceErrors.length === 0 && (
              <Button size="sm" disabled={Boolean(busy)} onClick={() => run(() => axios.post(`${base}/einvoice`), 'IRN generated', 'irn')}>
                {busy === 'irn' ? 'Working...' : 'Generate IRN'}
              </Button>
            )}
            {irnActive && cancelling !== 'einvoice' && canCancel && (
              pastDeadline(status.einvoiceCancellableUntil) ? (
                <div className="small text-muted">
                  The 24 hours for cancelling this IRN ended {when(status.einvoiceCancellableUntil)}. To reverse or correct the sale,
                  issue a credit note against it and register that.
                </div>
              ) : (
                <>
                  <Button size="sm" variant="outline-danger" disabled={Boolean(busy) || Boolean(ewbActive)} onClick={() => setCancelling('einvoice')}
                    title={ewbActive ? 'Cancel the e-way bill first' : 'An IRN can be cancelled within 24 hours'}>
                    Cancel IRN
                  </Button>
                  {status.einvoiceCancellableUntil && <span className="small text-muted ms-2">until {when(status.einvoiceCancellableUntil)}</span>}
                </>
              )
            )}
              </>
            )}

            {status.ewayOnly && !status.ewayApplicable && (
              <p className="text-muted small">E-way bills are switched off. Turn them on in Org settings &gt; GST Tax Profile.</p>
            )}
            {status.ewayApplicable && (
              <>
                {!status.ewayOnly && <hr />}
                <h6>e-Way bill</h6>
                {status.ewayBlocked && !ewbActive && <Alert variant="warning" className="small py-2">{status.ewayBlocked}</Alert>}
                {status.ewayAlwaysRequired && !ewb && (
                  <p className="small text-muted mb-2">The goods go to another state for job work, so an e-way bill is needed whatever their value.</p>
                )}
                {ewb && (
                  <div className="small mb-2">
                    <Badge bg={ewbActive ? 'info' : 'secondary'} className="me-2">{ewbActive ? 'Active' : 'Cancelled'}</Badge>
                    {ewb.sandbox && <Badge bg="warning" text="dark">Sandbox</Badge>}
                    <div className="mt-1"><strong>EWB no:</strong> {ewb.ewbNo} &nbsp; <strong>Date:</strong> {when(ewb.ewbDate)} &nbsp; <strong>Valid until:</strong> {when(ewb.validUpto)}</div>
                    <div><strong>Mode:</strong> {ewb.transportMode} &nbsp; <strong>Vehicle:</strong> {ewb.vehicleNo || '-'} &nbsp; <strong>Distance:</strong> {ewb.distanceKm} km</div>
                  </div>
                )}
                {ewbActive && cancelling !== 'eway' && <EwayChanges base={base} ewb={ewb} run={run} busy={busy} />}
                {ewbActive && cancelling !== 'eway' && canCancel && (
                  pastDeadline(status.ewayCancellableUntil) ? (
                    <div className="small text-muted">
                      The 24 hours for cancelling this e-way bill ended {when(status.ewayCancellableUntil)}. If the goods didn&apos;t
                      move, it lapses when its validity ends; the buyer can also reject it on the e-way bill portal within 72 hours.
                    </div>
                  ) : (
                    <>
                      <Button size="sm" variant="outline-danger" disabled={Boolean(busy)} onClick={() => setCancelling('eway')}>Cancel e-way bill</Button>
                      {status.ewayCancellableUntil && <span className="small text-muted ms-2">until {when(status.ewayCancellableUntil)}</span>}
                    </>
                  )
                )}
                {!ewbActive && !status.ewayBlocked && (
                  <>
                    <Row className="g-2">
                      <Col md={3}>
                        <Form.Label className="small mb-1">Mode</Form.Label>
                        <Form.Select size="sm" {...field('mode')}>
                          <option value="ROAD">Road</option>
                          <option value="RAIL">Rail</option>
                          <option value="AIR">Air</option>
                          <option value="SHIP">Ship</option>
                        </Form.Select>
                      </Col>
                      <Col md={3}>
                        <Form.Label className="small mb-1">Distance (km)</Form.Label>
                        <Form.Control size="sm" type="number" min="1" max="4000" {...field('distanceKm')} />
                      </Col>
                      <Col md={3}>
                        <Form.Label className="small mb-1">Vehicle no</Form.Label>
                        <Form.Control size="sm" placeholder="KA01AB1234" {...field('vehicleNo')} />
                      </Col>
                      <Col md={3}>
                        <Form.Label className="small mb-1">Transporter GSTIN</Form.Label>
                        <Form.Control size="sm" maxLength={15} {...field('transporterId')} />
                      </Col>
                      <Col md={6}>
                        <Form.Label className="small mb-1">Transporter name</Form.Label>
                        <Form.Control size="sm" {...field('transporterName')} />
                      </Col>
                      {transport.mode !== 'ROAD' && (
                        <>
                          <Col md={3}>
                            <Form.Label className="small mb-1">Transport doc no</Form.Label>
                            <Form.Control size="sm" {...field('transportDocNo')} />
                          </Col>
                          <Col md={3}>
                            <Form.Label className="small mb-1">Transport doc date</Form.Label>
                            <DateInput size="sm" {...field('transportDocDate')} />
                          </Col>
                        </>
                      )}
                    </Row>
                    <Form.Check
                      className="mt-2 small"
                      type="checkbox"
                      id="gst-eway-below"
                      label="Raise it even if the value is below the e-way bill threshold"
                      checked={confirmBelow}
                      onChange={(e) => setConfirmBelow(e.target.checked)}
                    />
                    <Button size="sm" className="mt-2" disabled={Boolean(busy)} onClick={generateEwb}>
                      {busy === 'ewb' ? 'Working...' : 'Generate e-way bill'}
                    </Button>
                  </>
                )}
              </>
            )}

            {cancelling && (
              <Alert variant="light" className="border mt-3">
                <div className="fw-bold mb-2">Cancel {cancelling === 'einvoice' ? 'the IRN' : 'the e-way bill'}</div>
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Select size="sm" value={reason} onChange={(e) => setReason(e.target.value)}>
                      {CANCEL_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </Form.Select>
                  </Col>
                  <Col md={8}>
                    <Form.Control size="sm" maxLength={100} placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
                  </Col>
                </Row>
                <div className="mt-2">
                  <Button size="sm" variant="danger" disabled={Boolean(busy)} className="me-2"
                    onClick={() => run(
                      () => axios.post(`${base}/${cancelling === 'einvoice' ? 'einvoice' : 'eway'}/cancel`, { reasonCode: reason, remarks }),
                      cancelling === 'einvoice' ? 'IRN cancelled' : 'E-way bill cancelled',
                      'cancel'
                    )}>
                    Confirm cancellation
                  </Button>
                  <Button size="sm" variant="outline-secondary" onClick={() => setCancelling(null)}>Keep it</Button>
                </div>
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}

/** Whether the organization has e-invoicing / e-way bills switched on. */
export function useGstEnabled(active = true) {
  const [enabled, setEnabled] = useState({ einvoiceEnabled: false, ewayEnabled: false });
  useEffect(() => {
    if (!active) return;
    axios.get('/api/gst-api/enabled').then((r) => setEnabled(r.data || {})).catch(() => {});
  }, [active]);
  return enabled;
}

/** Org settings: switch e-invoicing and e-way bills on, and the credentials for the GST API. Saves on its own. */
export function GstApiSettingsCard() {
  const { has } = usePlan();
  const [form, setForm] = useState(null);
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState('');

  const apply = (data) => {
    setForm({
      einvoiceEnabled: Boolean(data.einvoiceEnabled),
      einvoiceStartDate: data.einvoiceStartDate || '',
      thirtyDayReporting: Boolean(data.thirtyDayReporting),
      ewayEnabled: Boolean(data.ewayEnabled),
      ewayThreshold: data.ewayThreshold ?? 50000,
      apiUsername: data.apiUsername || '',
      passwordSet: Boolean(data.passwordSet),
      provider: data.provider,
      sandbox: Boolean(data.sandbox)
    });
    setPassword('');
    setClearPassword(false);
  };

  useEffect(() => {
    axios.get('/api/gst-api/settings').then((r) => apply(r.data)).catch(() => setForm(null));
  }, []);

  if (!form) return null;

  const save = async () => {
    setSaving(true);
    setProblem('');
    try {
      const r = await axios.put('/api/gst-api/settings', {
        einvoiceEnabled: form.einvoiceEnabled,
        einvoiceStartDate: form.einvoiceStartDate || null,
        thirtyDayReporting: form.thirtyDayReporting,
        ewayEnabled: form.ewayEnabled,
        ewayThreshold: form.ewayThreshold === '' ? null : form.ewayThreshold,
        apiUsername: form.apiUsername,
        apiPassword: password || null,
        clearPassword
      });
      apply(r.data);
      toast.success('E-invoice and e-way bill settings saved');
    } catch (e) {
      setProblem(errorMessage(e, 'Could not save the settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border rounded p-3 mb-3">
      <h6 className="mb-1">e-Invoice and e-Way bill</h6>
      <p className="text-muted small">
        Register invoices and credit/debit notes with the GST portal (IRN and QR) and raise e-way bills for goods in transit.
        Provider: <strong>{form.sandbox ? 'Sandbox (nothing is sent to the government)' : form.provider}</strong>.
        Switch on e-invoicing only once your turnover crosses the limit that applies to you.
      </p>
      {problem && <Alert variant="danger" className="py-2">{problem}</Alert>}
      <FeatureNotice feature="E_INVOICE" className="mb-2">e-Invoices can’t be generated.</FeatureNotice>
      <FeatureNotice feature="EWAY_BILL" className="mb-2">e-Way bills can’t be generated.</FeatureNotice>
      <Row>
        <Col md={6}>
          <Form.Check
            type="switch" id="gst-api-einvoice" className="mb-2" label="Generate e-invoices (IRN)"
            checked={form.einvoiceEnabled} disabled={!has("E_INVOICE")} onChange={(e) => setForm({ ...form, einvoiceEnabled: e.target.checked })}
          />
          <Form.Group className="mb-3">
            <Form.Label className="small">Applies to documents dated from</Form.Label>
            <DateInput value={form.einvoiceStartDate} onChange={(e) => setForm({ ...form, einvoiceStartDate: e.target.value })} />
          </Form.Group>
          <Form.Check
            type="checkbox" id="gst-api-thirty-day" className="mb-3 small"
            label="Our turnover (AATO) is Rs 10 crore or more: IRNs must be generated within 30 days of the document date"
            checked={form.thirtyDayReporting} onChange={(e) => setForm({ ...form, thirtyDayReporting: e.target.checked })}
          />
        </Col>
        <Col md={6}>
          <Form.Check
            type="switch" id="gst-api-eway" className="mb-2" label="Generate e-way bills"
            checked={form.ewayEnabled} disabled={!has("EWAY_BILL")} onChange={(e) => setForm({ ...form, ewayEnabled: e.target.checked })}
          />
          <Form.Group className="mb-3">
            <Form.Label className="small">Consignment value above which an e-way bill is needed</Form.Label>
            <Form.Control type="number" min="0" value={form.ewayThreshold} onChange={(e) => setForm({ ...form, ewayThreshold: e.target.value })} />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="small">API username</Form.Label>
            <Form.Control type="text" autoComplete="off" value={form.apiUsername} onChange={(e) => setForm({ ...form, apiUsername: e.target.value })} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label className="small">API password</Form.Label>
            <Form.Control
              type="password" autoComplete="new-password" value={password} disabled={clearPassword}
              placeholder={form.passwordSet ? 'Saved - type to replace' : ''}
              onChange={(e) => setPassword(e.target.value)}
            />
            {form.passwordSet && (
              <Form.Check
                className="small mt-1" type="checkbox" id="gst-api-clear-pw" label="Remove the saved password"
                checked={clearPassword} onChange={(e) => setClearPassword(e.target.checked)}
              />
            )}
          </Form.Group>
        </Col>
      </Row>
      <Button size="sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save e-invoice settings'}</Button>
    </div>
  );
}
